import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef
} from "react"

import {filterAutocompleteOptions, normalizeCompanySearch} from "src/v2/utils/autocomplete/filter-autocomplete-options"
import {getCompanyOptionLabel} from "src/v2/utils/autocomplete/get-company-option-label"

const COMPANY_AUTOCOMPLETE_ACTIONS = {
    SET_INPUT_VALUE: "SET_INPUT_VALUE",
    SET_SELECTED_VALUE: "SET_SELECTED_VALUE",
    SELECT_COMPANY: "SELECT_COMPANY",
    CLEAR_SELECTION: "CLEAR_SELECTION",
    SEARCH_START: "SEARCH_START",
    SEARCH_SUCCESS: "SEARCH_SUCCESS",
    SEARCH_ERROR: "SEARCH_ERROR",
    SEARCH_CLEAR: "SEARCH_CLEAR",
    RESET_STATE: "RESET_STATE",
}

const INITIAL_STATE = {
    inputValue: "",
    selectedValue: null,
    results: [],
    loading: false,
    error: null,
}

const reducerHandlers = {
    [COMPANY_AUTOCOMPLETE_ACTIONS.SET_INPUT_VALUE]: (state, payload) => ({
        ...state,
        inputValue: payload ?? "",
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SET_SELECTED_VALUE]: (state, payload) => ({
        ...state,
        selectedValue: payload,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SELECT_COMPANY]: (state, payload) => ({
        ...state,
        selectedValue: payload?.company ?? null,
        inputValue: payload?.inputValue ?? "",
        results: [],
        loading: false,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.CLEAR_SELECTION]: (state) => ({
        ...state,
        selectedValue: null,
        inputValue: "",
        results: [],
        loading: false,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_START]: (state) => ({
        ...state,
        loading: true,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_SUCCESS]: (state, payload) => ({
        ...state,
        results: Array.isArray(payload) ? payload : [],
        loading: false,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_ERROR]: (state, payload) => ({
        ...state,
        results: [],
        loading: false,
        error: payload || "Erro ao pesquisar empresas.",
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_CLEAR]: (state) => ({
        ...state,
        results: [],
        loading: false,
        error: null,
    }),

    [COMPANY_AUTOCOMPLETE_ACTIONS.RESET_STATE]: () => ({
        ...INITIAL_STATE,
    }),
}


const companyAutocompleteReducer = (state, action) => {
    const handler = reducerHandlers[action.type]

    return handler
        ? handler(state, action.payload)
        : state
}

const getCompanyKey = (company) =>
    String(
        company?.CPFCNPJ ??
        company?.formattedCPFCNPJ ??
        company?.RazaoSocial ??
        ""
    )


export const useCompanyAutocomplete = ({
    companies = [],
    companiesLoading = false,
    onCompanySelect,
    debounceDelay = 300,
    minSearchLength = 1,
    maxResults = 10,
} = {}) => {
    const wasLoadingRef = useRef(companiesLoading)

    const [state, dispatch] = useReducer(
        companyAutocompleteReducer,
        INITIAL_STATE
    )

    const debounceRef = useRef(null)
    const lastInputValueRef = useRef("")

    const actions = useMemo(
        () => ({
            setInputValue: (value) =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SET_INPUT_VALUE,
                    payload: value,
                }),

            setSelectedValue: (value) =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SET_SELECTED_VALUE,
                    payload: value,
                }),

            startSearch: () =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_START,
                }),

            searchSuccess: (results) =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_SUCCESS,
                    payload: results,
                }),

            searchError: (message) =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_ERROR,
                    payload: message,
                }),

            clearSearch: () =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.SEARCH_CLEAR,
                }),

            resetState: () =>
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.RESET_STATE,
                }),
        }),
        []
    )

    const searchCompanies = useCallback(
        (value) => {
            if (!Array.isArray(companies)) return []

            const foundKeys = new Set()
            const filteredCompanies = filterAutocompleteOptions(companies, value, {
                filterKeys: ["RazaoSocial", "CPFCNPJ", "formattedCPFCNPJ"],
                getOptionLabel: getCompanyOptionLabel,
                normalizeFilter: normalizeCompanySearch,
            })

            return filteredCompanies
                .filter((company) => {
                    const key = getCompanyKey(company)
                    if (!key || foundKeys.has(key)) return false
                    foundKeys.add(key)
                    return true
                })
                .slice(0, maxResults)
        },
        [companies, maxResults]
    )

    const selectCompany = useCallback(
        (company) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
            }

            if (!company) {
                lastInputValueRef.current = ""
                dispatch({
                    type: COMPANY_AUTOCOMPLETE_ACTIONS.CLEAR_SELECTION,
                })
                onCompanySelect?.(null)
                return
            }

            const label = getCompanyOptionLabel(company)
            lastInputValueRef.current = label
            dispatch({
                type: COMPANY_AUTOCOMPLETE_ACTIONS.SELECT_COMPANY,
                payload: { company, inputValue: label },
            })
            onCompanySelect?.(company)
        },
        [onCompanySelect]
    )

    const clearSelection = useCallback(() => {
        selectCompany(null)
    }, [selectCompany])

    const handleInputChange = useCallback(
        (_event, newInputValue, reason) => {
            if (reason === "reset") return

            const input = newInputValue ?? ""
            actions.setInputValue(input)

            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
            }

            if (reason === "clear") {
                clearSelection()
                return
            }

            if (input.trim().length < minSearchLength) {
                actions.clearSearch()
                return
            }

            if (companiesLoading) {
                actions.clearSearch()
                return
            }

            debounceRef.current = setTimeout(() => {
                actions.startSearch()

                try {
                    const results = searchCompanies(input)
                    actions.searchSuccess(results)
                } catch (error) {
                    console.error("Erro ao pesquisar empresa:", error)
                    actions.searchError(error?.message || "Erro ao pesquisar empresas.")
                } finally {
                    debounceRef.current = null
                }
            }, debounceDelay)
        },
        [actions, clearSelection, companiesLoading, debounceDelay, minSearchLength, searchCompanies]
    )

    const handleSelectionChange = useCallback(
        (_event, newValue, reason) => {
            if (reason === "clear" || !newValue) {
                selectCompany(null)
                return
            }

            selectCompany(newValue)
        },
        [selectCompany]
    )

    const isOptionEqualToValue = useCallback(
        (option, value) =>
            getCompanyKey(option) === getCompanyKey(value),
        []
    )

    const cleanup = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }

        actions.resetState()
    }, [actions])

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [])

    
    useEffect(() => {
        const finishedLoading = wasLoadingRef.current && !companiesLoading
        wasLoadingRef.current = companiesLoading

        const input = state.inputValue.trim()
        if (!finishedLoading || input.length < minSearchLength) return

        actions.searchSuccess(searchCompanies(input))
    }, [actions, companiesLoading, minSearchLength, searchCompanies, state.inputValue])

    const computed = useMemo(
        () => ({
            hasResults: state.results.length > 0,
            hasError: Boolean(state.error),
            isEmpty:
                !state.inputValue &&
                !state.selectedValue,
            isSearching: state.loading,
        }),
        [state]
    )

    return {
        state,
        actions,
        selectCompany,
        clearSelection,
        handlers: {
            onInputChange: handleInputChange,
            onSelectionChange: handleSelectionChange,
            getOptionLabel: getCompanyOptionLabel,
            isOptionEqualToValue,
        },
        computed,
        cleanup,
    }
}

export default useCompanyAutocomplete